// src/pages/Enseignants/PrepaPhysique/EditExerciseModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { editExercise } from "../../../services/prepaPhysiqueServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const initialForm = {
  nom: "",
  programme: "",
  description: "",
  lien_photo: "",
  lien_video: "",
};

// Quill (mêmes outils/formats que tes autres modales)
const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const EditExerciseModal = ({ isOpen, onClose, exercise, refresh }) => {
  const [formValues, setFormValues] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);

  // Hydrate le formulaire quand on ouvre / change d’exercice
  useEffect(() => {
    if (!isOpen || !exercise) return;
    setFormValues({
      nom: exercise.nom || "",
      programme: exercise.programme || "",
      description: exercise.description || "",
      lien_photo: exercise.lien_photo || "",
      lien_video: exercise.lien_video || "",
    });
  }, [isOpen, exercise]);

  // Ouverture : focus, ESC, verrouillage du scroll
  useEffect(() => {
    if (!isOpen) return;

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setFormValues(initialForm);
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!exercise?.id) return;
    if (!formValues.nom.trim()) {
      toast.error("Veuillez renseigner le nom de l'exercice.");
      return;
    }

    try {
      setSaving(true);
      await editExercise(exercise.id, formValues);
      toast.success("Exercice modifié avec succès !");
      refresh?.();
      handleClose();
    } catch (error) {
      console.error("Erreur modification exercice:", error);
      toast.error("Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !exercise) return null;

  // PORTAL
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier un exercice"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-[860px] flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold">Modifier un Exercice</h3>
          <Button
            text="Fermer"
            icon={FaTimes}
            onClick={handleClose}
            className="!rounded-xl bg-white/10 hover:bg-white/20 text-white gap-2"
          />
        </div>

        {/* Corps scrollable */}
        <form onSubmit={handleSubmit} className="grow overflow-y-auto p-5 space-y-4">
          {/* Nom / Programme */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1 opacity-70">Nom *</label>
              <input
                type="text"
                name="nom"
                placeholder="Nom de l'exercice"
                value={formValues.nom}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-1 opacity-70">Programme</label>
              <input
                type="text"
                name="programme"
                placeholder="Thème / programme (optionnel)"
                value={formValues.programme}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>
          </div>

          {/* Description (Quill) */}
          <div>
            <label className="block text-xs mb-1 opacity-70">Description</label>
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700">
              <ReactQuill
                key={`rq-${isOpen}-${exercise?.id || "edit"}`}
                theme="snow"
                value={formValues.description}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, description: value }))
                }
                placeholder="Description détaillée de l'exercice"
                modules={quillModules}
                formats={quillFormats}
                className="bg-white dark:bg-slate-900"
              />
            </div>
          </div>

          {/* Liens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1 opacity-70">URL de l'image</label>
              <input
                type="url"
                name="lien_photo"
                placeholder="https://…"
                value={formValues.lien_photo}
                onChange={handleChange}
                inputMode="url"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs mb-1 opacity-70">URL de la vidéo</label>
              <input
                type="url"
                name="lien_video"
                placeholder="https://…"
                value={formValues.lien_video}
                onChange={handleChange}
                inputMode="url"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>
          </div>

          {/* Espace pour éviter le recouvrement par le footer sticky sur mobile */}
          <div className="h-12 sm:h-0" />
        </form>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 border-t border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
          <Button
            text="Annuler"
            icon={FaTimes}
            onClick={handleClose}
            type="button"
            className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
          />
          <Button
            text={saving ? "Enregistrement..." : "Enregistrer"}
            icon={FaSave}
            type="submit"
            disabled={saving}
            className="!rounded-xl gap-2"
            onClick={() => dialogRef.current?.querySelector("form")?.requestSubmit()}
          />
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        .grow.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .grow.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditExerciseModal;
